// Module identity registry.
//
// One row per top-level admin module. Each entry carries the visual identity
// used across all surfaces that reference the module: sidebar nav, launchpad
// tiles, recent-visits chips, settings chooser, and any future module-aware
// surface (breadcrumbs, search results, audit chips, etc.).
//
// SVG paths are sized for a 24×24 viewBox with `currentColor` strokes so a
// single component can render the icon at any size and color the caller asks
// for. Colors are stored as Tailwind gradient class strings so consumers can
// drop them straight into `bg-gradient-to-br ${gradient}`.
//
// Tailwind safelist note: the gradient + tint class strings below are read
// by Tailwind's JIT scanner because they appear as literal strings in this
// source file. If you add new color stops, also confirm Tailwind has emitted
// them on first build.

import React from 'react';

// ─── Module ids ─────────────────────────────────────────────────────────────

export type ModuleId =
  | 'home'
  | 'dashboard'
  | 'bills'
  | 'tenants'
  | 'users'
  | 'platform-users'
  | 'billing-platform'
  | 'my-subscription'
  | 'locations'
  | 'products'
  | 'categories'
  | 'variant-attributes'
  | 'customers'
  | 'loyalty'
  | 'vouchers'
  | 'memberships'
  | 'communications'
  | 'commissions'
  | 'shifts'
  | 'restaurant'
  | 'festive'
  | 'credit-accounts'
  | 'corporate-accounts'
  | 'exchange-rates'
  | 'staff-performance'
  | 'discounts'
  | 'expenses'
  | 'quotations'
  | 'sales-orders'
  | 'sales-invoices'
  | 'approvals'
  | 'inventory-dashboard'
  | 'reports'
  | 'notifications'
  | 'audit'
  | 'sync-health'
  | 'backups'
  | 'data-export'
  | 'dead-letters'
  | 'system-health'
  | 'settings'
  | 'platform-email'
  | 'platform-company-profile'
  | 'platform-enquiries'
  | 'website-pages'
  | 'online-orders'
  | 'product-masters'
  | 'inventory-valuation'
  | 'inventory-report'
  | 'inventory-low-stock'
  | 'inventory-expiry'
  | 'inventory-transfers'
  | 'inventory-wastage'
  | 'inventory-suppliers'
  | 'rfq'
  | 'purchase-order'
  | 'vendor-bills';

// ─── Palette ────────────────────────────────────────────────────────────────
// Stored as named constants so multiple modules can share a gradient without
// duplicating the literal string. Tailwind JIT picks up the literals from the
// const declarations.

const G_BRAND = 'from-brand-500 to-indigo-500';        // overview / sales
const G_INDIGO = 'from-indigo-500 to-violet-500';      // platform / admin
const G_VIOLET = 'from-violet-500 to-pink-500';        // restaurant / engagement
const G_PINK = 'from-pink-500 to-danger-500';          // CRM / loyalty
const G_TEAL = 'from-teal-500 to-success-500';         // inventory / catalog
const G_SUCCESS = 'from-success-500 to-teal-500';      // money in / billing
const G_WARN = 'from-warning-500 to-danger-500';       // expenses / alerts
const G_INFO = 'from-info-500 to-brand-500';           // people / health
const G_OPS = 'from-neutral-500 to-indigo-500';        // governance / infra
const G_HEAT = 'from-danger-500 to-warning-500';       // notifications / urgent

// Faint background tint matching each gradient. Used for the tile body so
// adjacent tiles read as colored cards rather than uniform white.
const T_BRAND = 'bg-brand-50/50 dark:bg-brand-900/10';
const T_INDIGO = 'bg-indigo-50/50 dark:bg-indigo-900/10';
const T_VIOLET = 'bg-violet-50/50 dark:bg-violet-900/10';
const T_PINK = 'bg-pink-50/50 dark:bg-pink-900/10';
const T_TEAL = 'bg-teal-50/50 dark:bg-teal-900/10';
const T_SUCCESS = 'bg-success-50/50 dark:bg-success-900/10';
const T_WARN = 'bg-warning-50/50 dark:bg-warning-900/10';
const T_INFO = 'bg-info-50/50 dark:bg-info-900/10';
const T_OPS = 'bg-neutral-100/60 dark:bg-neutral-900/40';
const T_HEAT = 'bg-danger-50/50 dark:bg-danger-900/10';

const TINT_BY_GRADIENT: Record<string, string> = {
  [G_BRAND]: T_BRAND,
  [G_INDIGO]: T_INDIGO,
  [G_VIOLET]: T_VIOLET,
  [G_PINK]: T_PINK,
  [G_TEAL]: T_TEAL,
  [G_SUCCESS]: T_SUCCESS,
  [G_WARN]: T_WARN,
  [G_INFO]: T_INFO,
  [G_OPS]: T_OPS,
  [G_HEAT]: T_HEAT,
};

// ─── Definitions ────────────────────────────────────────────────────────────

interface ModuleIconDef {
  /** One or more SVG path `d` strings, drawn in order with currentColor strokes. */
  paths: string[];
  /** Tailwind gradient class string: `from-X to-Y`. */
  gradient: string;
}

export const MODULE_ICONS: Record<ModuleId, ModuleIconDef> = {
  home: {
    paths: ['M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10'],
    gradient: G_BRAND,
  },
  dashboard: {
    // Pie chart, so it never looks like the Home house.
    paths: [
      'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z',
      'M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z',
    ],
    gradient: G_INDIGO,
  },
  bills: {
    paths: [
      'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_BRAND,
  },
  tenants: {
    paths: [
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    ],
    gradient: G_INDIGO,
  },
  users: {
    paths: [
      'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    ],
    gradient: G_INFO,
  },
  'platform-users': {
    // Person in a circle, so it never looks like the Audit shield.
    paths: [
      'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    ],
    gradient: G_INDIGO,
  },
  'billing-platform': {
    paths: [
      'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    ],
    gradient: G_SUCCESS,
  },
  'my-subscription': {
    // Badge with a tick.
    paths: [
      'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
    ],
    gradient: G_SUCCESS,
  },
  locations: {
    paths: ['M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'],
    gradient: G_PINK,
  },
  products: {
    paths: ['M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'],
    gradient: G_TEAL,
  },
  categories: {
    paths: [
      'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    ],
    gradient: G_TEAL,
  },
  'variant-attributes': {
    paths: [
      'M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
    ],
    gradient: G_VIOLET,
  },
  customers: {
    paths: [
      'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z',
    ],
    gradient: G_PINK,
  },
  loyalty: {
    paths: ['M12 2l2.39 7.36H22l-6.19 4.5 2.39 7.36L12 16.72 5.8 21.22l2.39-7.36L2 9.36h7.61z'],
    gradient: G_WARN,
  },
  vouchers: {
    // Ticket.
    paths: [
      'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z',
    ],
    gradient: G_PINK,
  },
  memberships: {
    paths: ['M5 3l3.5 4.5L12 3l3.5 4.5L19 3v13a2 2 0 01-2 2H7a2 2 0 01-2-2V3z'],
    gradient: G_VIOLET,
  },
  communications: {
    paths: [
      'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    ],
    gradient: G_VIOLET,
  },
  commissions: {
    paths: [
      'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    ],
    gradient: G_SUCCESS,
  },
  shifts: {
    paths: ['M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'],
    gradient: G_INFO,
  },
  restaurant: {
    paths: ['M3 3h18v18H3V3zm0 6h18M9 9v12'],
    gradient: G_VIOLET,
  },
  festive: {
    paths: [
      'M20 12v10H4V12m16 0L12 2 4 12m8-10v10m-8 0h16M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
    ],
    gradient: G_VIOLET,
  },
  'credit-accounts': {
    // Open book — the ledger a credit customer runs.
    paths: [
      'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    ],
    gradient: G_WARN,
  },
  'corporate-accounts': {
    paths: [
      'M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v.01M12 14v.01M16 14v.01M8 18v.01M12 18v.01M16 18v.01',
    ],
    gradient: G_INDIGO,
  },
  'exchange-rates': {
    paths: ['M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4'],
    gradient: G_WARN,
  },
  'staff-performance': {
    paths: ['M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'],
    gradient: G_INFO,
  },
  discounts: {
    // Percent sign.
    paths: [
      'M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z',
    ],
    gradient: G_PINK,
  },
  expenses: {
    paths: [
      'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    ],
    gradient: G_WARN,
  },
  quotations: {
    // Page with a pencil — a draft offer, not a bill.
    paths: [
      'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    ],
    gradient: G_SUCCESS,
  },
  'sales-orders': {
    // Clipboard with a check — a confirmed customer order.
    paths: [
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    ],
    gradient: G_BRAND,
  },
  'sales-invoices': {
    // Receipt with a tick — money owed to us for goods sold on sales orders.
    paths: ['M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-3-3-3 3-3-3-3 3V5a2 2 0 012-2h10a2 2 0 012 2v16z'],
    gradient: G_SUCCESS,
  },
  approvals: {
    paths: ['M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'],
    gradient: G_SUCCESS,
  },
  'inventory-dashboard': {
    paths: ['M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'],
    gradient: G_TEAL,
  },
  reports: {
    paths: [
      'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_BRAND,
  },
  notifications: {
    paths: ['M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0'],
    gradient: G_HEAT,
  },
  audit: {
    paths: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    gradient: G_OPS,
  },
  'sync-health': {
    paths: [
      'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.582m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    ],
    gradient: G_INFO,
  },
  backups: {
    paths: ['M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'],
    gradient: G_OPS,
  },
  'data-export': {
    paths: ['M20 16.2A4.5 4.5 0 0017.5 8H17a6 6 0 10-11.4 3M8 17l4 4 4-4M12 12v9'],
    gradient: G_OPS,
  },
  'dead-letters': {
    paths: ['M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01'],
    gradient: G_HEAT,
  },
  'system-health': {
    paths: ['M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18'],
    gradient: G_OPS,
  },
  settings: {
    paths: [
      'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    ],
    gradient: G_INDIGO,
  },
  'platform-email': {
    paths: [
      'M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    ],
    gradient: G_INFO,
  },
  'platform-company-profile': {
    paths: [
      'M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1M9 13h1m4 0h1M9 17h1m4 0h1',
    ],
    gradient: G_INDIGO,
  },
  'platform-enquiries': {
    paths: [
      'M8 10h8M8 14h5M21 12a9 9 0 01-13.5 7.8L3 21l1.2-4.5A9 9 0 1121 12z',
    ],
    gradient: G_VIOLET,
  },
  'website-pages': {
    paths: [
      'M4 5h16M4 12h16M4 19h10',
    ],
    gradient: G_BRAND,
  },
  'online-orders': {
    paths: [
      'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.3 2.3A1 1 0 005.4 17H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
    ],
    gradient: G_BRAND,
  },
  'product-masters': {
    paths: [
      'M4 6h16M4 10h16M4 14h10M4 18h10M17 15l2 2 4-4',
    ],
    gradient: G_TEAL,
  },
  'inventory-valuation': {
    // Scale — what the stock is worth.
    paths: [
      'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
    ],
    gradient: G_SUCCESS,
  },
  'inventory-report': {
    // Bars on a stand.
    paths: [
      'M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',
    ],
    gradient: G_TEAL,
  },
  'inventory-low-stock': {
    paths: [
      'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    ],
    gradient: G_WARN,
  },
  'inventory-expiry': {
    // Calendar.
    paths: [
      'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    ],
    gradient: G_HEAT,
  },
  'inventory-transfers': {
    paths: [
      'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
    ],
    gradient: G_TEAL,
  },
  'inventory-wastage': {
    paths: [
      'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
    ],
    gradient: G_WARN,
  },
  'inventory-suppliers': {
    paths: [
      'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    ],
    gradient: G_INFO,
  },
  rfq: {
    paths: [
      'M8 10h8m-8 4h5m-9 7l3-3h9a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v15z',
    ],
    gradient: G_INDIGO,
  },
  'purchase-order': {
    // Shopping bag — goods we buy in.
    paths: [
      'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
    ],
    gradient: G_SUCCESS,
  },
  'vendor-bills': {
    // Page with a down arrow — a bill that comes in.
    paths: [
      'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_WARN,
  },
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Render the registered SVG icon for `id` at the requested size. Strokes use
 * `currentColor` so wrap in a container with `text-white` (etc.) to color.
 */
export function ModuleIcon({
  id,
  className = 'h-5 w-5',
  strokeWidth = 1.5,
}: {
  id: ModuleId;
  className?: string;
  strokeWidth?: number;
}) {
  const def = MODULE_ICONS[id];
  if (!def) return null;
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      aria-hidden="true"
    >
      {def.paths.map((d, i) => (
        <path key={i} strokeLinecap="round" strokeLinejoin="round" d={d} />
      ))}
    </svg>
  );
}

/** Tailwind `from-X to-Y` for the module. Safe for unknown ids (falls back). */
export function moduleGradient(id: string): string {
  return (MODULE_ICONS as Record<string, ModuleIconDef>)[id]?.gradient ?? G_BRAND;
}

/** Faint background tint matching the module's gradient. */
export function moduleTint(id: string): string {
  return TINT_BY_GRADIENT[moduleGradient(id)] ?? T_BRAND;
}

// ─── Tailwind safelist crumbs ───────────────────────────────────────────────
// The literal class strings above are picked up by the JIT scanner from the
// `const` declarations. Keep this comment block so the gradients and tints
// stay grep-able in source.
//
// gradients: from-brand-500 to-indigo-500 from-indigo-500 to-violet-500
//   from-violet-500 to-pink-500 from-pink-500 to-danger-500
//   from-teal-500 to-success-500 from-success-500 to-teal-500
//   from-warning-500 to-danger-500 from-info-500 to-brand-500
//   from-neutral-500 to-indigo-500 from-danger-500 to-warning-500
//
// tints: bg-brand-50/50 bg-indigo-50/50 bg-violet-50/50 bg-pink-50/50
//   bg-teal-50/50 bg-success-50/50 bg-warning-50/50 bg-info-50/50
//   bg-neutral-100/60 bg-danger-50/50
//   dark:bg-brand-900/10 dark:bg-indigo-900/10 dark:bg-violet-900/10
//   dark:bg-pink-900/10 dark:bg-teal-900/10 dark:bg-success-900/10
//   dark:bg-warning-900/10 dark:bg-info-900/10 dark:bg-neutral-900/40
//   dark:bg-danger-900/10
